import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { FieldDef, FieldSize, FieldVariant, GridSpan } from './types';

/** App-wide field settings: one place keeps every form looking the same. */
export interface FieldDefaults {
  size: FieldSize;
  variant: FieldVariant;
  fullWidth: boolean;
  shrinkLabel: boolean;
  className?: string;
  grid: GridSpan;
  /** Per-type defaults, e.g. `{ money: { config: { decimals: 0 } } }`. */
  types?: Record<string, Partial<FieldDef>>;
}

export const BASE_FIELD_DEFAULTS: FieldDefaults = {
  size: 'small',
  variant: 'outlined',
  fullWidth: true,
  shrinkLabel: true,
  grid: { xs: 12, md: 6 },
};

const FieldDefaultsContext = createContext<FieldDefaults>(BASE_FIELD_DEFAULTS);

/** Override defaults for everything inside; nested providers merge onto their parent's. */
export function FieldDefaultsProvider({ value, children }: { value: Partial<FieldDefaults>; children: ReactNode }) {
  const parent = useContext(FieldDefaultsContext);
  const merged = useMemo<FieldDefaults>(() => ({
    ...parent,
    ...value,
    grid: { ...parent.grid, ...value.grid },
    types: { ...parent.types, ...value.types },
  }), [parent, value]);
  return <FieldDefaultsContext.Provider value={merged}>{children}</FieldDefaultsContext.Provider>;
}

export function useFieldDefaults(): FieldDefaults {
  return useContext(FieldDefaultsContext);
}
